import React from 'react';
import ActionSheet, { Action } from 'components/v2/ActionSheet/ActionSheet';
import { useNavigate } from 'utils/router';
import { applyFilters, Filters } from 'core';
import { useUserIsAdmin, useUserIsEmployee } from 'modules/auth/hooks';
import { useTranslation } from 'utils/translation';
import { useDataContext } from '../../../components/v2/Data/DataContext';
import useDirectoryIntelligenceFieldsForm from '../../intelligent-layer/hooks/useDirectoryIntelligenceFieldsForm';
import { Box } from '@mui/material';
import Icon from 'components/v2/Icon/Icon';
import { useRepairRequest } from 'plugins/device/src/components/RepairRequestForm';
import { useMaintenanceRequest } from 'plugins/device/src/components/MaintenanceRequestForm';
import { route } from 'router';
import { useReportContext } from '../../reports/context/ReportContext';
import { Dir } from 'modules/dirs';
import useDirectoryPermissions from '../../dirs/hooks/useDirectoryPermissions';
import usePermissions from '../../auth/permissions';
import { useDirsActions } from '../../dirs/hooks';
import wait from '../../../utils/wait';
import { refetchActiveQueries } from '../../../utils/QueryClientProvider';
import useConfirmation from '../../../components/v2/Confirmation/use-confirmation';
import { useAppFeatures } from '../../ui/selectors/app';

type Props = {
  item: Dir;
  refetch?(): void;
  extraActions?: Action[];
};

const ItemActions: React.FC<Props> = ({ item, refetch, extraActions = [] }) => {
  const { id, directoryType } = item;
  const { t } = useTranslation();
  const { can } = usePermissions();
  const report = useReportContext();
  const navigate = useNavigate();
  const isAdmin = useUserIsAdmin();
  const isEmployee = useUserIsEmployee();
  const openRepairRequest = useRepairRequest(id);
  const { deleteDir } = useDirsActions();
  const confirm = useConfirmation();
  const features = useAppFeatures();
  const openPermissions = useDirectoryPermissions(item);
  const openMaintenanceRequest = useMaintenanceRequest(id);
  const dataContext = useDataContext();
  const openFieldsForm = useDirectoryIntelligenceFieldsForm(id, {
    enableTitle: ['object-type', 'composite'].includes(directoryType),
    onSuccess() {
      dataContext.refresh();
      refetch?.();
    },
  });

  const onDelete = async () => {
    const confirmed = await confirm();

    if (confirmed) {
      deleteDir({ id });
      await wait(200);
      dataContext.refresh();
      void refetchActiveQueries();
    }
  };

  const onOpenDetails = () => {
    if (
      item.directoryType === 'unique-object' ||
      // legacy msi case
      item.directoryType === 'job-submission'
    ) {
      return navigate(
        route('report.item.details', { id: report.id, item: id }),
      );
    }

    if (item.directoryType === 'object-type') {
      return navigate(route('assets.types.details', { id }));
    }

    if (item.directoryType === 'composite') {
      return navigate(route('assets.composites.details', { id }));
    }

    console.error('Not implemented details case for: ', item);
  };

  const actions = [
    {
      icon: <Icon name="fa-solid fa-search" />,
      label: t('Details'),
      action: onOpenDetails,
    },
    ...extraActions,
    {
      icon: <Icon name="fa-regular fa-pencil" />,
      label: t('Edit'),
      action: openFieldsForm,
      disabled: !(isAdmin || item.metadata?.isEditable),
    },
    features.permissionsOnAssetLevel && isEmployee
      ? {
          icon: <Icon name="fa-solid fa-person" />,
          label: t('Permissions'),
          action: openPermissions,
          visible: can('manage directory permissions'),
        }
      : null,
    {
      icon: <Icon name="fa-solid fa-screwdriver-wrench" />,
      label: t('maintenance_request'),
      action: openMaintenanceRequest,
      visible: directoryType === 'unique-object',
    },
    {
      icon: <Icon name="fa-solid fa-toolbox" />,
      label: t('service_request'),
      action: openRepairRequest,
      visible: directoryType === 'unique-object',
    },
    {
      icon: <Icon name="fa-solid fa-trash" />,
      label: t('delete'),
      action: onDelete,
      disabled: !(isAdmin || item.metadata?.isEditable),
    },
  ].filter(Boolean) as Action[];

  const finalActions = applyFilters(Filters.reports.extraItemActions, actions, {
    isAdmin,
    id,
  });

  return (
    <Box sx={{ textAlign: 'right' }}>
      <ActionSheet actions={finalActions} />
    </Box>
  );
};

export default ItemActions;
